import { useCallback, useEffect, useState } from "react";
import {
    Alert, Box, Button, Card, CircularProgress, Grid, IconButton, InputAdornment, InputBase, Stack, Switch, Table, TableBody, TableCell,
    TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Add, CheckCircle, Delete, PlaylistAdd, WarningAmber } from "@mui/icons-material";
import { getData, postData } from "../../services/base.service";
import { useNotification } from "../Shared/NotificationProvider";
import { MarginLine, ProjectMargin } from "../../types/marginTypes";
import { ProjectBilling } from "../../types/billingTypes";
import { formatMoney, formatPercent, numberOrNull } from "./projectUtils";

type ProjectMarginTabProps = {
    projectId: number;
    isEditMode: boolean;
    setLoading: Function;
};

const money = (value: number) => Math.round(value).toLocaleString('de-DE');
const pct = (value: number | null) => (value === null ? '-' : formatPercent(value, 1));

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'success' | 'warning' | 'error' }) {
    return (
        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
            <Box sx={{ padding: 1.5 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: tone ? `${tone}.main` : 'text.primary' }}>{value}</Typography>
                {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            </Box>
        </Card>
    );
}

// Number cell that saves on blur (edit mode) and shows the value otherwise.
function NumberCell({ value, editable, onSave, width = 70, suffix }: { value: number | null; editable: boolean; onSave: (v: number | null) => void; width?: number; suffix?: string }) {
    const [text, setText] = useState(value === null ? '' : String(value));
    useEffect(() => { setText(value === null ? '' : String(value)); }, [value]);
    if (!editable) return <span>{value === null ? '-' : `${money(value)}${suffix ?? ''}`}</span>;
    return (
        <InputBase value={text} onChange={(e) => setText(e.target.value)} inputProps={{ inputMode: 'decimal', style: { textAlign: 'right', padding: '2px 4px' } }}
            onBlur={() => {
                const next = numberOrNull(text.replace(',', '.'));
                if (next !== value) onSave(next);
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            sx={{ width, fontSize: 14, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }} />
    );
}

function TextCell({ value, editable, onSave, width = 150 }: { value: string | null; editable: boolean; onSave: (v: string | null) => void; width?: number }) {
    const [text, setText] = useState(value ?? '');
    useEffect(() => { setText(value ?? ''); }, [value]);
    if (!editable) return <span>{value || '-'}</span>;
    return (
        <InputBase value={text} onChange={(e) => setText(e.target.value)} onBlur={() => { if ((text || null) !== value) onSave(text.trim() || null); }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            inputProps={{ style: { padding: '2px 4px' } }}
            sx={{ width, fontSize: 14, border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper' }} />
    );
}

// Internal margin calculation (replaces margin_calc Excel): one line per expert / scenario,
// internal cost rates and backstopping on the project level.
function ProjectMarginTab({ projectId, isEditMode, setLoading }: ProjectMarginTabProps) {
    const theme = useTheme();
    const notify = useNotification();
    const [margin, setMargin] = useState<ProjectMargin | null>(null);
    const [latestPeriodId, setLatestPeriodId] = useState<number | null>(null);
    const [settingsForm, setSettingsForm] = useState({ int_cost_rate: '', backstopping_rate: '', planned_backstopping_days: '', target_margin: '' });

    const load = useCallback(async () => {
        try {
            const result = await getData(`projects/${projectId}/margin`) as ProjectMargin;
            setMargin(result);
            setSettingsForm({
                int_cost_rate: String(result.settings.int_cost_rate),
                backstopping_rate: String(result.settings.backstopping_rate),
                planned_backstopping_days: String(result.settings.planned_backstopping_days),
                target_margin: String(Math.round(result.settings.target_margin * 1000) / 10),
            });
        } catch (error) {
            notify(`Couldn't load the margin: ${(error as Error).message}`, 'error');
        }
    }, [projectId, notify]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        getData(`projects/${projectId}/billing`).then((res: ProjectBilling) => setLatestPeriodId(res.periods[res.periods.length - 1]?.ID ?? null)).catch(() => setLatestPeriodId(null));
    }, [projectId]);

    const run = async (work: () => Promise<unknown>, success?: string) => {
        setLoading(true);
        try {
            await work();
            if (success) notify(success, 'success');
            await load();
        } catch (error) {
            notify((error as Error).message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const updateLine = (line: MarginLine, patch: Partial<MarginLine>) =>
        run(() => postData(`projects/${projectId}/margin/lines/update`, { id: line.ID, ...patch }));

    const saveSettings = () => {
        const target = numberOrNull(settingsForm.target_margin.replace(',', '.'));
        run(() => postData(`projects/${projectId}/margin/settings`, {
            int_cost_rate: numberOrNull(settingsForm.int_cost_rate) ?? 0,
            backstopping_rate: numberOrNull(settingsForm.backstopping_rate) ?? 0,
            planned_backstopping_days: numberOrNull(settingsForm.planned_backstopping_days.replace(',', '.')) ?? 0,
            target_margin: target === null ? 0.25 : Math.min(1, Math.max(0, target / 100)),
        }), 'Internal rates saved.');
    };

    if (!margin) return <Box display="flex" justifyContent="center" paddingY={6}><CircularProgress /></Box>;
    const { settings, lines, totals } = margin;
    const profitTone = totals.meetsTarget === null ? undefined : totals.meetsTarget ? 'success' : totals.plannedNetProfit < 0 ? 'error' : 'warning';
    const backstoppingOk = totals.maxBackstoppingDays === null || settings.planned_backstopping_days <= totals.maxBackstoppingDays;

    const settingField = (key: keyof typeof settingsForm, label: string, unit: string) => (
        <TextField size="small" fullWidth label={label} value={settingsForm[key]} disabled={!isEditMode}
            onChange={(e) => setSettingsForm({ ...settingsForm, [key]: e.target.value })} onBlur={() => isEditMode && saveSettings()}
            InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }} />
    );

    const headerCell = (label: string, tip?: string, align: 'left' | 'right' | 'center' = 'right') => (
        <TableCell align={align} sx={{ whiteSpace: 'nowrap', fontWeight: 600, fontSize: 13 }}>
            {tip ? <Tooltip title={tip}><span style={{ borderBottom: '1px dotted' }}>{label}</span></Tooltip> : label}
        </TableCell>
    );

    return (
        <Box>
            {/* Headline figures */}
            <Grid container spacing={1.5} sx={{ marginBottom: 2.5 }}>
                <Grid item xs={6} md={3}>
                    <Tile label="Planned net profit" value={formatMoney(totals.plannedNetProfit, 'EUR')} tone={profitTone}
                        sub={`after ${formatMoney(totals.backstoppingCost, 'EUR')} backstopping`} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <Tile label="Planned margin on INT share" value={pct(totals.plannedMarginPct)} tone={profitTone}
                        sub={`target ${pct(settings.target_margin)} · overall ${pct(totals.plannedMarginPctOverall)}`} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <Tile label="Net margin before backstopping" value={formatMoney(totals.netMargin, 'EUR')}
                        sub={`gross ${formatMoney(totals.grossMargin, 'EUR')} (${pct(totals.grossMarginPct)})`} />
                </Grid>
                <Grid item xs={6} md={3}>
                    <Tile label="Max backstopping days" value={totals.maxBackstoppingDays === null ? '-' : String(totals.maxBackstoppingDays)}
                        tone={backstoppingOk ? undefined : 'error'}
                        sub={`${settings.planned_backstopping_days} planned · keeps the ${pct(settings.target_margin)} target`} />
                </Grid>
            </Grid>

            {totals.meetsTarget === false && (
                <Alert severity={totals.plannedNetProfit < 0 ? 'error' : 'warning'} icon={<WarningAmber />} sx={{ marginBottom: 2 }}>
                    The planned margin of {pct(totals.plannedMarginPct)} is below the target of {pct(settings.target_margin)}.
                </Alert>
            )}
            {totals.meetsTarget === true && (
                <Alert severity="success" icon={<CheckCircle />} sx={{ marginBottom: 2 }}>
                    The planned margin of {pct(totals.plannedMarginPct)} meets the target of {pct(settings.target_margin)}.
                </Alert>
            )}

            {/* Internal rates */}
            <Card variant="outlined" sx={{ borderRadius: 2, padding: 2, marginBottom: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 1.5 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Internal rates and backstopping</Typography>
                    {settings.updated_by && <Typography variant="caption" color="text.secondary">last change by {settings.updated_by}</Typography>}
                </Stack>
                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>{settingField('int_cost_rate', 'INT cost rate', '€/day')}</Grid>
                    <Grid item xs={6} md={3}>{settingField('backstopping_rate', 'Backstopping cost rate', '€/day')}</Grid>
                    <Grid item xs={6} md={3}>{settingField('planned_backstopping_days', 'Planned backstopping days', 'days')}</Grid>
                    <Grid item xs={6} md={3}>{settingField('target_margin', 'Target net margin', '%')}</Grid>
                </Grid>
            </Card>

            {/* Scenarios */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ marginBottom: 1 }}>
                <Typography variant="h6">Experts / scenarios</Typography>
                {isEditMode && (
                    <Stack direction="row" gap={1}>
                        {latestPeriodId && (
                            <Button size="small" variant="outlined" startIcon={<PlaylistAdd />}
                                onClick={() => run(() => postData(`projects/${projectId}/margin/prefill`, { contractPeriodId: latestPeriodId }), 'Positions taken over from the cost plan.')}>
                                Take over from cost plan
                            </Button>
                        )}
                        <Button size="small" variant="contained" disableElevation startIcon={<Add />}
                            onClick={() => run(() => postData(`projects/${projectId}/margin/lines/add`, { int_cost_rate: null }))}>Line</Button>
                    </Stack>
                )}
            </Stack>
            {lines.length === 0 ? (
                <Typography color="text.secondary" sx={{ paddingY: 2 }}>
                    No scenarios yet. {isEditMode ? 'Add lines or take the positions over from the cost plan.' : 'Switch to edit mode to start the calculation.'}
                </Typography>
            ) : (
                <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 2 }}>
                    <Table size="small">
                        <TableHead sx={{ backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
                            <TableRow>
                                {headerCell('', undefined, 'left')}
                                {headerCell('Expert', undefined, 'left')}
                                {headerCell('INT expert', 'Yes: INT invoices the full fee. No: the expert is paid by the client/partner, INT keeps only the mark-up.', 'center')}
                                {headerCell('RfS days', 'Days in the request for services / contract')}
                                {headerCell('Ext. days', 'Days worked by the external expert (paid at the expert rate)')}
                                {headerCell('INT days', 'Days worked by INT staff (at the INT cost rate)')}
                                {headerCell('Margin days', 'RfS days - external days - INT days')}
                                {headerCell('Selling rate')}
                                {headerCell('Expert rate')}
                                {headerCell('INT cost rate', 'Empty = project INT cost rate')}
                                {headerCell('Revenue')}
                                {headerCell("INT's share")}
                                {headerCell('Expert costs')}
                                {headerCell('Gross %')}
                                {headerCell('Total cost')}
                                {headerCell('Net margin')}
                                {headerCell('Net %')}
                                {isEditMode && <TableCell />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {lines.map((line) => (
                                <TableRow key={line.ID} hover>
                                    <TableCell sx={{ fontWeight: 600 }}>{line.scenario}</TableCell>
                                    <TableCell>
                                        <TextCell value={line.expert_name} editable={isEditMode} onSave={(v) => updateLine(line, { expert_name: v })} />
                                        {line.comment && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{line.comment}</Typography>}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Switch size="small" checked={line.via_int} disabled={!isEditMode} onChange={(e) => updateLine(line, { via_int: e.target.checked })} />
                                    </TableCell>
                                    <TableCell align="right"><NumberCell value={line.rfs_days} editable={isEditMode} width={60} onSave={(v) => updateLine(line, { rfs_days: v ?? 0 })} /></TableCell>
                                    <TableCell align="right"><NumberCell value={line.external_days} editable={isEditMode} width={60} onSave={(v) => updateLine(line, { external_days: v ?? 0 })} /></TableCell>
                                    <TableCell align="right"><NumberCell value={line.int_days} editable={isEditMode} width={60} onSave={(v) => updateLine(line, { int_days: v ?? 0 })} /></TableCell>
                                    <TableCell align="right" sx={{ color: line.intMarginDays < 0 ? 'error.main' : 'text.primary', fontWeight: 600 }}>{line.intMarginDays}</TableCell>
                                    <TableCell align="right"><NumberCell value={line.selling_rate} editable={isEditMode} onSave={(v) => updateLine(line, { selling_rate: v ?? 0 })} /></TableCell>
                                    <TableCell align="right"><NumberCell value={line.expert_rate} editable={isEditMode} onSave={(v) => updateLine(line, { expert_rate: v ?? 0 })} /></TableCell>
                                    <TableCell align="right">
                                        {isEditMode
                                            ? <NumberCell value={line.int_cost_rate} editable onSave={(v) => updateLine(line, { int_cost_rate: v })} />
                                            : <span style={{ color: line.int_cost_rate === null ? theme.palette.text.secondary : undefined }}>{money(line.effectiveIntCostRate)}</span>}
                                    </TableCell>
                                    <TableCell align="right">{money(line.revenue)}</TableCell>
                                    <TableCell align="right">{money(line.intShare)}</TableCell>
                                    <TableCell align="right">{money(line.expertCosts)}</TableCell>
                                    <TableCell align="right">{pct(line.grossMarginPct)}</TableCell>
                                    <TableCell align="right">{money(line.totalCost)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 600, color: line.netMargin < 0 ? 'error.main' : 'text.primary' }}>{money(line.netMargin)}</TableCell>
                                    <TableCell align="right">{pct(line.netMarginPct)}</TableCell>
                                    {isEditMode && (
                                        <TableCell>
                                            <IconButton size="small" color="error" onClick={() => window.confirm(`Remove ${line.scenario} ${line.expert_name ?? ''}?`) && run(() => postData(`projects/${projectId}/margin/lines/remove`, { id: line.ID }))}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    )}
                                </TableRow>
                            ))}
                            <TableRow sx={{ backgroundColor: 'action.hover', '& td': { fontWeight: 700 } }}>
                                <TableCell />
                                <TableCell>Total</TableCell>
                                <TableCell />
                                <TableCell align="right">{totals.rfsDays}</TableCell>
                                <TableCell align="right">{lines.reduce((s, l) => s + l.external_days, 0)}</TableCell>
                                <TableCell align="right">{lines.reduce((s, l) => s + l.int_days, 0)}</TableCell>
                                <TableCell align="right">{lines.reduce((s, l) => s + l.intMarginDays, 0)}</TableCell>
                                <TableCell colSpan={3} />
                                <TableCell align="right">{money(totals.revenue)}</TableCell>
                                <TableCell align="right">{money(totals.intShare)}</TableCell>
                                <TableCell align="right">{money(totals.expertCosts)}</TableCell>
                                <TableCell align="right">{pct(totals.grossMarginPct)}</TableCell>
                                <TableCell align="right">{money(totals.totalCost)}</TableCell>
                                <TableCell align="right">{money(totals.netMargin)}</TableCell>
                                <TableCell align="right">{pct(totals.netMarginPct)}</TableCell>
                                {isEditMode && <TableCell />}
                            </TableRow>
                        </TableBody>
                    </Table>
                </Box>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', marginTop: 1 }}>
                Planned net profit = net margin - planned backstopping days x backstopping cost rate. Max backstopping days = days that still keep the target net margin on INT's share.
            </Typography>
        </Box>
    );
}

export default ProjectMarginTab;
